import React, { useMemo, useState } from "react";
import { FaSearch, FaEye, FaPen } from "react-icons/fa";
import {
  SETTING_KEY_CATALOG,
  ALL_SETTING_KEY_PATHS,
  normalizeSettingKeyPaths,
} from "./settingKeyCatalog";

/**
 * Rol şablonu → "Ayarlar" sekmesi: cihaz ayarlarına anahtar bazlı erişim.
 *
 * Her anahtarın İKİ kutucuğu var:
 *   Görür  → ayar panelde görünür (yoksa hiç listelenmez)
 *   Yazar  → ayar değiştirilebilir; işaretlenince "Görür" otomatik açılır
 *            (yazabildiğin şeyi göremezsen kaydedemezsin)
 *
 * Liste STATİK katalogdan gelir (settingKeyCatalog.js) — referans cihaz seçmeye
 * gerek yok. Katalog cihaz uygulamasının ayar şemasından üretildi.
 *
 * Sunucu her iki listeyi de bağımsız uygular (device-setting-scope.js):
 * READ süzgeci settingReadKeys, WRITE süzgeci settingWriteKeys. Yani panelde
 * gizlemek tek başına yeterli değil — izinsiz ayar cihaza hiç yollanmaz.
 */
export default function SettingKeysPicker({
  readKeys = [],
  writeKeys = [],
  onChange,
}) {
  const [search, setSearch] = useState("");
  // Eski anahtar adları (#253) yeni karşılığıyla işaretli görünür; kaydedilince
  // yeni adla yazılır.
  const readSet = useMemo(
    () => new Set(normalizeSettingKeyPaths(readKeys)),
    [readKeys],
  );
  const writeSet = useMemo(
    () => new Set(normalizeSettingKeyPaths(writeKeys)),
    [writeKeys],
  );

  const q = search.trim().toLocaleLowerCase("tr-TR");
  const groups = useMemo(() => {
    if (!q) return SETTING_KEY_CATALOG;
    return SETTING_KEY_CATALOG.map((g) => ({
      ...g,
      keys: g.keys.filter(
        (k) =>
          k.label.toLocaleLowerCase("tr-TR").includes(q) ||
          k.key.toLocaleLowerCase("tr-TR").includes(q),
      ),
    })).filter((g) => g.keys.length > 0);
  }, [q]);

  const emit = (nextRead, nextWrite) =>
    onChange({ readKeys: [...nextRead], writeKeys: [...nextWrite] });

  const toggleRead = (path) => {
    const nextRead = new Set(readSet);
    const nextWrite = new Set(writeSet);
    if (nextRead.has(path)) {
      // Görme kapanınca yazma da kapanır — göremediğini kaydedemezsin.
      nextRead.delete(path);
      nextWrite.delete(path);
    } else {
      nextRead.add(path);
    }
    emit(nextRead, nextWrite);
  };

  const toggleWrite = (path) => {
    const nextRead = new Set(readSet);
    const nextWrite = new Set(writeSet);
    if (nextWrite.has(path)) {
      nextWrite.delete(path);
    } else {
      nextWrite.add(path);
      nextRead.add(path); // yazma → görme otomatik
    }
    emit(nextRead, nextWrite);
  };

  const setGroup = (paths, kind, on) => {
    const nextRead = new Set(readSet);
    const nextWrite = new Set(writeSet);
    paths.forEach((p) => {
      if (kind === "read") {
        if (on) nextRead.add(p);
        else {
          nextRead.delete(p);
          nextWrite.delete(p);
        }
      } else if (on) {
        nextWrite.add(p);
        nextRead.add(p);
      } else {
        nextWrite.delete(p);
      }
    });
    emit(nextRead, nextWrite);
  };

  const HeaderToggle = ({ label, icon, active, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-[11px] font-SemiBold px-2 py-0.5 rounded-full border transition-colors ${
        active
          ? "bg-indigo-50 text-indigo-700 border-indigo-200"
          : "bg-white text-gray-500 border-gray-200 hover:text-gray-700"
      }`}
    >
      {icon} {label}
    </button>
  );

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-800">
          Cihaz Ayarları Erişimi
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">
          <b>Görür</b> işaretli değilse ayar cihaz ayarları ekranında hiç
          görünmez. <b>Yazar</b> işaretlenince görme otomatik açılır. Seçim
          yapılmazsa ayarlar sekmesi boş kalır.
        </p>
        <p className="text-[11px] text-gray-400 mt-1">
          Seçili: {readSet.size} görür · {writeSet.size} yazar /{" "}
          {ALL_SETTING_KEY_PATHS.length} ayar
        </p>
      </div>

      <div className="relative">
        <FaSearch className="w-3 h-3 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Ayar ara…"
          className="w-full pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      <div className="space-y-4">
        {groups.map((grp) => {
          const paths = grp.keys.map((k) => `${grp.group}.${k.key}`);
          const allRead = paths.every((p) => readSet.has(p));
          const allWrite = paths.every((p) => writeSet.has(p));
          return (
            <div key={grp.group} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <span className="text-sm font-SemiBold text-gray-700">
                  {grp.label}
                  <span className="text-xs text-gray-400 font-Default ml-1.5">
                    ({paths.filter((p) => readSet.has(p)).length}/{paths.length})
                  </span>
                </span>
                <div className="flex items-center gap-1.5">
                  <HeaderToggle
                    label="Tümünü gör"
                    icon={<FaEye className="w-2.5 h-2.5" />}
                    active={allRead}
                    onClick={() => setGroup(paths, "read", !allRead)}
                  />
                  <HeaderToggle
                    label="Tümünü yaz"
                    icon={<FaPen className="w-2.5 h-2.5" />}
                    active={allWrite}
                    onClick={() => setGroup(paths, "write", !allWrite)}
                  />
                </div>
              </div>

              <div className="space-y-0.5">
                {grp.keys.map((k) => {
                  const path = `${grp.group}.${k.key}`;
                  return (
                    <div
                      key={path}
                      className="flex items-center justify-between gap-3 px-2 py-1.5 rounded-lg hover:bg-gray-50"
                      title={path}
                    >
                      <span className="text-xs text-gray-700 truncate min-w-0">
                        {k.label}
                      </span>
                      <div className="flex items-center gap-3 shrink-0">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={readSet.has(path)}
                            onChange={() => toggleRead(path)}
                            className="h-3.5 w-3.5 accent-gray-500"
                          />
                          <span className="text-[11px] text-gray-500">Görür</span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={writeSet.has(path)}
                            onChange={() => toggleWrite(path)}
                            className="h-3.5 w-3.5 accent-indigo-500"
                          />
                          <span className="text-[11px] text-gray-500">Yazar</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
